'use client'

import { useCallback, useContext } from 'react'
import { usePathname } from 'next/navigation'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { deleteCartAction, modifyCartAction } from '@/app/_actions/cart.actions'
import { MUTATION_KEYS, QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { modifyCartTracking } from '@/utils/tracking.utils'
import { transformAdditionalInfoForCart } from '@/utils/components/tickets.utils'
import { getLineItemsByCategory } from '@/utils/cart.utils'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import { AppError, unwrapActionResult } from '@/utils/errors.utils'
import { ErrorType } from '@/types/errors.types'
import useHandleError from '@/hooks/useHandleError'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'

export type UseCartActionsProps = {
  sellerId: Nullable<string>
  onSuccess?: () => void
  onDeleteAllTickets?: () => void
}

type ModifyCartVars = {
  ticketIdsToRemove?: string[]
  itemsToReserve?: Transformed.LineItem[]
}

export type UseCartActionsReturn = {
  modifyCart: (vars?: ModifyCartVars) => Promise<Transformed.Cart>
  deleteTickets: (ticketIds: string[]) => Promise<void>
  deleteCart: () => Promise<void>
  isModifyPending: boolean
  isDeletePending: boolean
  isPending: boolean
  error: Error | null
}

const useCartActions = ({
  sellerId,
  onSuccess,
  onDeleteAllTickets,
}: UseCartActionsProps): UseCartActionsReturn => {
  const queryClient = useQueryClient()
  const pathname = usePathname()
  const cartId = useOurStore((state) => state.cartId)
  const localLineItems = useOurStore((state) => state.localLineItems)
  const lineItemsToDelete = useOurStore((state) => state.lineItemsToDelete)
  const additionalInfoResponses = useOurStore((state) => state.additionalInfoResponses)
  const admissionType = useOurStore((state) => state.admissionType)
  const resetByGroups = useOurStore((state) => state.resetByGroups)
  const resetByKeys = useOurStore((state) => state.resetByKeys)
  const setCartId = useOurStore((state) => state.setCartId)
  const { user } = useContext(LoginContext)
  const { handleError } = useHandleError()
  const { cart } = useCart()

  const modifyCartMutation = useMutation({
    mutationKey: [MUTATION_KEYS.ADD_TICKETS],
    mutationFn: async (vars?: ModifyCartVars) => {
      if (!sellerId) throw new AppError(ErrorType.AddTicketsToCart, 'sellerId is required to add tickets')
      const itemsToReserve = vars?.itemsToReserve ?? localLineItems

      return await modifyCartAction({
        cartId: cartId ?? undefined,
        sellerId,
        localLineItems: itemsToReserve,
        ticketIdsToRemove: vars?.ticketIdsToRemove,
        additionalInfo: transformAdditionalInfoForCart(additionalInfoResponses, itemsToReserve),
      }).then(unwrapActionResult)
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData([QUERY_KEYS.CART, updatedCart.id], updatedCart)

      modifyCartTracking({
        localLineItems,
        lineItemsToDelete,
        cart: updatedCart,
        pathname,
        admissionType,
        origin: window.location.origin,
      })

      setCartId(updatedCart.id, !!user)
      resetByGroups(['localItems'])

      onSuccess?.()
    },
    onError: (err) => {
      handleError(err, ErrorType.AddTicketsToCart)
    },
  })

  const deleteCartMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_CART],
    mutationFn: (cartIdToDelete: string) => deleteCartAction(cartIdToDelete).then(unwrapActionResult),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.CART] })
      resetByGroups(['cart', 'dateAndTime'])
      onDeleteAllTickets?.()
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to delete cart', { error: err, cartId })
    },
  })

  const removeTicketsMutation = useMutation({
    mutationKey: [MUTATION_KEYS.REMOVE_TICKETS],
    mutationFn: async (ticketIds: string[]) => {
      if (!cartId || !sellerId) throw new AppError(ErrorType.AddTicketsToCart, 'cartId and sellerId are required to remove tickets')
      const result = await modifyCartAction({ cartId, sellerId, localLineItems: [], ticketIdsToRemove: ticketIds })
      return unwrapActionResult(result)
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData([QUERY_KEYS.CART, updatedCart.id], updatedCart)

      modifyCartTracking({
        localLineItems: [],
        lineItemsToDelete,
        cart: updatedCart,
        pathname,
        admissionType,
        origin: window.location.origin,
      })

      resetByKeys(['lineItemsToDelete'])

      const { tickets, events } = getLineItemsByCategory({ lineItems: updatedCart.lineItems })
      if (tickets.length === 0 && events.length === 0 && cartId) {
        deleteCartMutation.mutate(cartId)
      }
    },
    onError: (err) => {
      handleError(err, ErrorType.AddTicketsToCart)
    },
  })

  const modifyCart = useCallback(
    (vars?: ModifyCartVars) => modifyCartMutation.mutateAsync(vars),
    [modifyCartMutation.mutateAsync],
  )

  const deleteTickets = useCallback(
    async (ticketIds: string[]) => { await removeTicketsMutation.mutateAsync(ticketIds) },
    [removeTicketsMutation.mutateAsync],
  )

  const deleteCart = useCallback(
    () => {
      if (!cartId) return Promise.resolve()
      // Track any removed tickets
      modifyCartTracking({
        localLineItems,
        lineItemsToDelete,
        cart,
        pathname,
        admissionType,
        origin: window.location.origin,
      })
      return deleteCartMutation.mutateAsync(cartId)
    },
    [cartId, localLineItems, lineItemsToDelete, cart, pathname, admissionType, deleteCartMutation.mutateAsync],
  )

  const isModifyPending = modifyCartMutation.isPending
  const isDeletePending = removeTicketsMutation.isPending || deleteCartMutation.isPending

  return {
    modifyCart,
    deleteTickets,
    deleteCart,
    isModifyPending,
    isDeletePending,
    isPending: isModifyPending || isDeletePending,
    error: modifyCartMutation.error ?? removeTicketsMutation.error ?? deleteCartMutation.error,
  }
}

export default useCartActions
